import React from 'react';
import {
    Input
} from 'shared/components/ui/input';
import {
    Label
} from 'shared/components/ui/label';
import {
    Switch
} from 'shared/components/ui/switch';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    MemoryConfig
} from '../types/builderAssistant.types';
import ChipInput from './ChipInput';

interface MemoryConfigSectionProps {
  value: MemoryConfig;
  onChange: (next: MemoryConfig) => void;
}

// Strategies other than `window` are declared in the schema for forward
// compat but Mind only implements `window` today. Expose all, disable the
// rest in the UI until Mind catches up.
const STRATEGIES: Array<{ value: MemoryConfig['strategy']; label: string; enabled: boolean }> = [
    {
 value: 'window',
label: 'Window (last N turns)',
enabled: true
},
    {
 value: 'summary',
label: 'Summary (coming soon)',
enabled: false
},
    {
 value: 'full',
label: 'Full (coming soon)',
enabled: false
},
    {
 value: 'hybrid',
label: 'Hybrid (coming soon)',
enabled: false
},
];

export default function MemoryConfigSection({
 value, onChange
}: MemoryConfigSectionProps) {
    const update = (patch: Partial<MemoryConfig>) => onChange({
 ...value,
...patch
});
    const updateGov = (patch: Partial<MemoryConfig['governance']>) =>
        onChange({
 ...value,
governance: {
 ...value.governance,
...patch
}
});

    return (
        <div className="space-y-4">
            <div className="space-y-2">
                <Label>Strategy</Label>
                <Select
                    value={value.strategy}
                    onValueChange={(v) => update({
 strategy: v as MemoryConfig['strategy']
})}
                >
                    <SelectTrigger>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {STRATEGIES.map(s => (
                            <SelectItem key={s.value} value={s.value} disabled={!s.enabled}>
                                {s.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                    <Label>Max turns</Label>
                    <Input
                        type="number"
                        min={1}
                        max={1000}
                        value={value.max_turns}
                        onChange={(e) => update({
 max_turns: Math.max(1, Math.min(1000, Number(e.target.value) || 1))
})}
                    />
                </div>
                <div className="space-y-1">
                    <Label>Max tokens</Label>
                    <Input
                        type="number"
                        min={100}
                        max={100000}
                        value={value.max_tokens}
                        onChange={(e) => update({
 max_tokens: Math.max(100, Math.min(100000, Number(e.target.value) || 100))
})}
                    />
                </div>
                <div className="space-y-1">
                    <Label>TTL (seconds)</Label>
                    <Input
                        type="number"
                        min={60}
                        value={value.ttl}
                        onChange={(e) => update({
 ttl: Math.max(60, Number(e.target.value) || 60)
})}
                    />
                </div>
            </div>

            <div className="flex items-center justify-between">
                <div>
                    <Label>Employee-scope memory</Label>
                    <div className="text-xs text-muted-foreground">
                        Persist a cross-conversation summary the assistant can draw from.
                    </div>
                </div>
                <Switch
                    checked={value.employee_memory}
                    onCheckedChange={(v) => update({
 employee_memory: !!v
})}
                />
            </div>

            <div className="space-y-2 pt-2 border-t">
                <div className="flex items-center gap-2">
                    <Label>Retention policy</Label>
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                        informational today
                    </span>
                </div>
                <p className="text-xs text-muted-foreground">
                    Stored on the assistant but not enforced at runtime yet. Future
                    memory strategies (summary / hybrid) will use these hints to
                    bias what gets kept vs. dropped during summarisation.
                </p>
                <div className="space-y-2">
                    <div>
                        <Label className="text-xs text-muted-foreground">Retain (free-text hints)</Label>
                        <ChipInput
                            values={value.governance.retain}
                            onChange={(vals) => updateGov({
 retain: vals
})}
                            placeholder="e.g. order references"
                        />
                    </div>
                    <div>
                        <Label className="text-xs text-muted-foreground">Forget (free-text hints)</Label>
                        <ChipInput
                            values={value.governance.forget}
                            onChange={(vals) => updateGov({
 forget: vals
})}
                            placeholder="e.g. credit card numbers"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
